import { Input } from '@/common/components/Input.tsx'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { SubmitHandler, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigate } from 'react-router-dom';
import { AppRoutePaths } from '@/routing/route_paths'
import { Label } from '@/shad/ui/label'
import { useSelector, useDispatch } from "react-redux"
import { RootState } from '@/storage/store'
import { setReimbursementAccountNumber } from '@/storage/applications/reimubrusementAccountSlice'
import FamilyWizardLayout from '@/app/pages/family_application/FamilyWizardLayout'
import { SubmitButton } from '@/app/pages/family_application/student_details/shared/submit-button'
import AccountNumberHelpIMG from '@assets/account_number.png'
import { Button } from '@/app/common/components/Button'

const detailSchema = z
  .object({
    account_number: z.string().min(1, 'account_number_is_required'),
  })

type FormValues = z.infer<typeof detailSchema>

export const ReimbursementAccountNumber = () => {
  const { t: translate } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { currentAccount } = useSelector((state: RootState) => state.ReimbursementAccount)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(detailSchema),
    defaultValues: {
      account_number: currentAccount?.accountNumber ?? "",
    }
  })

  const onSubmit: SubmitHandler<FormValues> = async (data: FormValues) => {
    dispatch(setReimbursementAccountNumber(data.account_number))
    navigate(AppRoutePaths.reimbursements.accounts.link.routing_number)
  }

  const onBack = () => {
    navigate(-1)
  }

  return (
    <FamilyWizardLayout>
      <FamilyWizardLayout.Header>
        <h1>{translate('whats_your_bank_account_number')}</h1>
      </FamilyWizardLayout.Header>
      <FamilyWizardLayout.Content className="w-100">
        <form
          id="form"
          onSubmit={handleSubmit(onSubmit)}
          className="space-y-3"
        >
          <div className="my-10 sm:px-20">
            <section
              className="flex flex-col mt-6 space-y-2"
              aria-labelledby="account_number_label"
            >
              <Label
                id="account_number_label"
                htmlFor="account_number"
              >
                {translate('account_number')}{' '}
                <span className="text-danger ml-1">*</span>
              </Label>
              <div className="relative mt-2 rounded-md shadow-sm">
                <Input
                  id="account_number"
                  {...register('account_number')}
                />
              </div>
              {errors.account_number && (
                <p className="mt-1 text-sm text-red-600">
                  {translate(errors.account_number.message ?? '')}
                </p>
              )}
            </section>

          </div>
          <div>
            <img
              alt="Account number"
              width="100%"
              src={AccountNumberHelpIMG}
              className="mx-auto h-auto mt-5"
            />
            <div className="font-medium text-gray-900 mt-2 text-center text-xs sm:text-base sm:mr-20">
              {translate('account_number')}
            </div>
          </div>
        </form>
      </FamilyWizardLayout.Content>
      <FamilyWizardLayout.ActionBar className="justify-center">
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={onBack}
            className="px-10"
          >
            {translate('back')}
          </Button>
          {' '}
          <SubmitButton
            form="form"
            status={'idle'}
            buttonTxt={translate('next')}
            className="px-10"
          />
        </div>
      </FamilyWizardLayout.ActionBar>
    </FamilyWizardLayout>
  )
}
